/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useMemo } from 'react';
import {
  type GoogleAdsPlacementKey,
  type TenantConfig,
  useGetTenantGoogleAdsPlacements,
  type GoogleAdsPlacement,
  GoogleAdsPlacements,
} from '@/hooks/useTenant';
import { getGAMConfigFromTenant, deriveDivIdPrefix, type GAMConfig } from '@/config/ads.config';

interface TenantContextValue {
  tenant: TenantConfig;
  gamConfig: GAMConfig | null;
  adPlacements: GoogleAdsPlacements | null;
  isAdsEnabled: boolean;
  isPlacementEnabled: (key: GoogleAdsPlacementKey) => boolean;
  /** Generic tenant feature-flag lookup (sectioned by game type). */
  isFeatureEnabled: (section: string, key: string) => boolean;
  /**
   * Pick'em full-navigation flag. When true, the pre-DKPLAT-347 navigation is
   * restored (Lobby/Recent tabs, in-game tabs shown immediately, aggregate
   * leaderboard, breadcrumb back navigation).
   */
  pickemFullNavigation: boolean;
  /**
   * Controls whether the in-game Pick'em progress bar stays inline with the
   * page content or pins above host-app chrome.
   */
  pickemProgressBarMode: 'inline' | 'pinned';
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

interface TenantProviderProps {
  tenant: TenantConfig;
  children: React.ReactNode;
}

/**
 * Transform API placement data into AdPlacements format
 * The backend now provides adUnitPath directly on each placement.
 */
const transformPlacementsToAdPlacements = (
  placements: GoogleAdsPlacement[] | undefined
): GoogleAdsPlacements | null => {
  if (!placements || placements.length === 0) {
    return null;
  }

  const result: Partial<GoogleAdsPlacements> = {};

  placements.forEach(placement => {
    const adUnitPath = placement.adUnitPath;

    if (!adUnitPath) return;

    // Filter out empty targeting values
    const targeting = placement.targeting
      ? Object.fromEntries(
          Object.entries(placement.targeting).filter(([_, value]) => value && value.trim() !== '')
        )
      : undefined;

    // Derive divIdPrefix from placement key. Skip placements without one — both
    // generateAdDivId and AdSlot dereference it, so an entry with no divIdPrefix
    // would throw downstream (matches the guard in usePickemContestCardAd).
    const divIdPrefix = deriveDivIdPrefix(placement as GoogleAdsPlacement);

    if (!divIdPrefix) return;

    const adPlacement: GoogleAdsPlacement = {
      placementKey: placement.placementKey,
      enabled: placement.enabled,
      adUnitPath,
      sizes: placement.sizes || [
        [320, 50],
        [728, 90],
      ],
      sizeMappings: placement.sizeMappings,
      divIdPrefix,
      targeting,
    };

    result[placement.placementKey] = adPlacement;
  });

  return result as GoogleAdsPlacements;
};

export const TenantProvider: React.FC<TenantProviderProps> = ({ tenant, children }) => {
  const gamConfig = getGAMConfigFromTenant(tenant);
  const isAdsEnabled = gamConfig?.enabled || false;
  const { data: placements } = useGetTenantGoogleAdsPlacements(tenant.code);

  // Transform API placements into AdPlacements format
  const adPlacements = useMemo(() => {
    return transformPlacementsToAdPlacements(placements);
  }, [placements]);

  const enabledMap = new Map<string, boolean>(
    (placements || []).map(p => [p.placementKey, p.enabled])
  );
  const isPlacementEnabled = (key: GoogleAdsPlacementKey) => enabledMap.get(key) === true;

  const isFeatureEnabled = (section: string, key: string) =>
    tenant.featureConfig?.[section]?.[key] === true;
  const pickemFullNavigation = isFeatureEnabled('pickem', 'enableFullNavigation');
  const rawPickemProgressBarMode = tenant.featureConfig?.pickem?.progressBarMode;
  const pickemProgressBarMode =
    rawPickemProgressBarMode === 'inline' || rawPickemProgressBarMode === 'pinned'
      ? rawPickemProgressBarMode
      : tenant.code === 'sky'
        ? 'pinned'
        : 'inline';

  return (
    <TenantContext.Provider
      value={{
        tenant,
        gamConfig,
        adPlacements,
        isAdsEnabled,
        isPlacementEnabled,
        isFeatureEnabled,
        pickemFullNavigation,
        pickemProgressBarMode,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
